/**
 * useSalesScope() - the one period + comparison + filter scope for a sales page, synced to the URL.
 *
 *   const { scope, setScope, applyPreset, clearFilters, presets, preset, queryString,
 *           periodLabel, compareLabel, comparison, days, activeFilters } = useSalesScope();
 *
 *   scope        { country, date_from, date_to, compare, platform, brand, category, sku, search, preset }
 *                Defaults: last 30 complete India days (yesterday back 29 days), compare 'previous',
 *                filters 'ALL' / ''. Default values are kept out of the URL so links stay short and
 *                the default period stays "live" (rolls forward each day).
 *   setScope(patch)  Merge a patch into the URL. '', null and 'ALL' remove a key. Patching dates without
 *                a preset marks the period as 'custom'. Rapid consecutive calls are preserved (the
 *                router setter does not queue, so we keep a pending copy like useSalesReport does).
 *   applyPreset(key) 'yesterday' | '7d' | '30d' | 'this_month' | 'last_month' | 'custom'.
 *   clearFilters()   Clears platform, brand, category, sku and search (keeps period and compare).
 *   queryString  "country=IN&date_from=...&date_to=...&compare=previous[&platform=..]" for the API.
 *   periodLabel  "1–30 Oct vs 1–30 Sep";  compareLabel "vs previous 30 days" / "vs same period last year".
 *   activeFilters [{ key, label }] for chips (human platform names).
 *
 * Also exported: SALES_PRESETS, presetRange(key, now), matchPreset(from, to, now), scopeToParams(scope).
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCountry } from '../../contexts/CountryContext';
import { platformLabel } from '../../config/platforms';
import { comparisonRange, compareWords, dayCount, indiaToday, isIsoDay, periodLabel as rangeLabel, shiftDay } from './format';

import { useChannels } from '../../hooks/useChannels';
export const SALES_PRESETS = [
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'custom', label: 'Custom' },
];
export const DEFAULT_PRESET = '30d';
const FILTER_KEYS = ['platform', 'brand', 'category', 'sku', 'search'];
export const SCOPE_KEYS = ['date_from', 'date_to', 'compare', ...FILTER_KEYS];

/** India calendar range for a preset; every preset ends on yesterday or earlier (complete days only). */
export const presetRange = (key, now = new Date()) => {
  const today = indiaToday(now);
  const yesterday = shiftDay(today, -1);
  switch (key) {
    case 'yesterday': return { date_from: yesterday, date_to: yesterday };
    case '7d': return { date_from: shiftDay(yesterday, -6), date_to: yesterday };
    case '30d': return { date_from: shiftDay(yesterday, -29), date_to: yesterday };
    case 'this_month': return { date_from: `${yesterday.slice(0, 7)}-01`, date_to: yesterday };
    case 'last_month': {
      const end = shiftDay(`${today.slice(0, 7)}-01`, -1);
      return { date_from: `${end.slice(0, 7)}-01`, date_to: end };
    }
    default: return null;
  }
};

export const matchPreset = (from, to, now = new Date()) => {
  const found = SALES_PRESETS.find(({ key }) => {
    const range = presetRange(key, now);
    return range && range.date_from === from && range.date_to === to;
  });
  return found ? found.key : 'custom';
};

const readScope = (params, country) => {
  const fallback = presetRange(DEFAULT_PRESET);
  const rawTo = params.get('date_to');
  const rawFrom = params.get('date_from');
  let dateTo = isIsoDay(rawTo) ? rawTo : fallback.date_to;
  let dateFrom = isIsoDay(rawFrom) ? rawFrom : (isIsoDay(rawTo) ? shiftDay(dateTo, -29) : fallback.date_from);
  if (dateFrom > dateTo) [dateFrom, dateTo] = [dateTo, dateFrom];
  const preset = params.get('preset') === 'custom' ? 'custom' : matchPreset(dateFrom, dateTo);
  return {
    country: country || 'IN',
    date_from: dateFrom,
    date_to: dateTo,
    compare: params.get('compare') === 'last_year' ? 'last_year' : 'previous',
    platform: params.get('platform') || 'ALL',
    brand: params.get('brand') || 'ALL',
    category: params.get('category') || 'ALL',
    sku: params.get('sku') || '',
    search: params.get('search') || '',
    preset,
  };
};

/** API params for a scope: drops 'ALL' / empty values and the UI-only preset. */
export const scopeToParams = scope => {
  const out = {};
  if (!scope) return out;
  ['country', ...SCOPE_KEYS].forEach(key => {
    const value = scope[key];
    if (value != null && value !== '' && value !== 'ALL') out[key] = String(value);
  });
  return out;
};

const isEmpty = value => value == null || value === '' || value === 'ALL';

export const useSalesScope = () => {
  useChannels();   // re-render when channel names or colours change (Settings → Channels)
  const [params, setParams] = useSearchParams();
  const pending = useRef(params);
  useEffect(() => { pending.current = params; }, [params]);
  const { country } = useCountry();
  const key = params.toString();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scope = useMemo(() => readScope(params, country), [key, country]);

  const setScope = useCallback(patch => {
    const next = new URLSearchParams(pending.current);
    const { preset, ...rest } = patch || {};
    if (preset && preset !== 'custom') {
      const range = presetRange(preset);
      next.delete('preset');
      if (preset === DEFAULT_PRESET || !range) { next.delete('date_from'); next.delete('date_to'); }
      else { next.set('date_from', range.date_from); next.set('date_to', range.date_to); }
    } else if (preset === 'custom') {
      // Pin the dates currently shown so the custom inputs start from them.
      const current = readScope(next, country);
      next.set('date_from', rest.date_from || current.date_from);
      next.set('date_to', rest.date_to || current.date_to);
      next.set('preset', 'custom');
    } else if ('date_from' in rest || 'date_to' in rest) {
      next.set('preset', 'custom');
    }
    Object.entries(rest).forEach(([name, value]) => {
      if (preset && preset !== 'custom' && (name === 'date_from' || name === 'date_to')) return;
      if (isEmpty(value) || (name === 'compare' && value === 'previous')) next.delete(name);
      else next.set(name, String(value));
    });
    pending.current = next;
    setParams(next, { replace: true });
  }, [setParams, country]);

  const applyPreset = useCallback(presetKey => setScope({ preset: presetKey }), [setScope]);
  const clearFilters = useCallback(() => setScope(Object.fromEntries(FILTER_KEYS.map(name => [name, '']))), [setScope]);

  const derived = useMemo(() => {
    const days = dayCount(scope.date_from, scope.date_to);
    const comparison = comparisonRange(scope.date_from, scope.date_to, scope.compare);
    const apiParams = scopeToParams(scope);
    const activeFilters = [];
    if (scope.platform !== 'ALL') activeFilters.push({ key: 'platform', label: platformLabel(scope.platform) });
    if (scope.brand !== 'ALL') activeFilters.push({ key: 'brand', label: scope.brand });
    if (scope.category !== 'ALL') activeFilters.push({ key: 'category', label: scope.category });
    if (scope.sku) activeFilters.push({ key: 'sku', label: `SKU ${scope.sku}` });
    if (scope.search) activeFilters.push({ key: 'search', label: `“${scope.search}”` });
    return {
      days,
      comparison,
      apiParams,
      queryString: new URLSearchParams(apiParams).toString(),
      periodLabel: rangeLabel(scope.date_from, scope.date_to, scope.compare, comparison),
      compareLabel: compareWords(scope.compare, days),
      activeFilters,
    };
  }, [scope]);

  return { scope, setScope, applyPreset, clearFilters, presets: SALES_PRESETS, preset: scope.preset, ...derived };
};

export default useSalesScope;
